import { useEffect, useState, useRef, useMemo, useLayoutEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import './Page3346Style.css';
import ApiSender from './../common/ApiSender';
import {
  getDailyPlan,
  sendPin as megacomSendPin,
  verifyPin as megacomVerifyPin,
  FALLBACK_DAILY_PLAN,
  OTP_LENGTH,
} from './../common/megacom';
import { toNationalNumber, sanitizeMsisdnInput } from './../common/msisdn';
import { carrierOf, isBeeline, isMegacom, resultPath } from './../common/carrier';
import { isFailedSubscribe } from './../common/failureRouting';

const MEGACOM_LP_NAME = 'KG 3346 LP - MegaCom';

function Page3346() {

  const [operator, setOperator] = useState(null);

  const [goalId, setGoalId] = useState(null);
  const [goalName, setGoalName] = useState(null);
  const nav = useNavigate();
  const location = useLocation();
  const otpContainerRef = useRef();
  const [msisdn, setMsisdn] = useState(null);
  const [warning, setWarning] = useState(false);
  const [otp, setOtp] = useState(null);
  const [firstStep, setFirstStep] = useState(true);
  const [secondStep, setSecondStep] = useState(false);
  const [megacomPlan, setMegacomPlan] = useState(FALLBACK_DAILY_PLAN);

  const { queryParams, paramsObject } = useMemo(() => {
    const p = new URLSearchParams(location.search);
    const obj = Object.fromEntries(p.entries());
    return { queryParams: p, paramsObject: obj };
  }, [location.search]);

  const apiSenderRef = useRef(null);
  if (!apiSenderRef.current) apiSenderRef.current = new ApiSender();
  const apiSender = apiSenderRef.current;

  useEffect(() => {
    if (queryParams.has("campaignId")) {
      const fetchData = async () => {
        try {
          const response = await fetch(process.env.REACT_APP_GOOGLE_BASE_URL + 'campaign/info?uuid=' + queryParams.get("campaignId"),
            {
              method: 'GET',
              headers: {
                "Content-Type": "application/json"
              }
            });
          if (response.ok) {
            const data = await response.json();
            const status = data.status;
            if (status) {
              setGoalId(data.goalId);
              setGoalName(data.goalName);
            }
          }
        }
        catch (error) {
        }
      }
      fetchData();
    }
  }, []);

  const historyUrl = useMemo(
    () => window.location.origin + location.pathname,
    [location.pathname]
  );

  useEffect(() => {
    const run = () => apiSender.saveHistory(historyUrl, paramsObject);

    if ("requestIdleCallback" in window) requestIdleCallback(run);
    else setTimeout(run, 0);
  }, [apiSender, historyUrl, paramsObject]);

  useLayoutEffect(() => {
    if (secondStep) otpContainerRef.current?.focus();
  }, [secondStep]);

  const handleMsisdnChange = (e) => {
    setWarning(false);
    const next = sanitizeMsisdnInput(e.target.value); // digits only, 9 (10 with a leading 0)
    setMsisdn((prev) => (prev === next ? prev : next));
  };

  const handleOtpChange = (e) => {
    setWarning(false);
    const next = e.target.value.replace(/\D/g, "").slice(0, OTP_LENGTH);
    setOtp((prev) => (prev === next ? prev : next));
  };


  // Plans are only needed by MegaCom numbers: fetch once, as soon as one is typed, so the answer
  // is usually back before the visitor clicks "Продолжить".
  const planRequestRef = useRef(null);
  const loadMegacomPlan = () => {
    if (!planRequestRef.current) {
      planRequestRef.current = getDailyPlan().then((plan) => {
        setMegacomPlan(plan);
        return plan;
      });
    }
    return planRequestRef.current;
  };

  useEffect(() => {
    if (isMegacom(msisdn)) {
      loadMegacomPlan();
    }
  }, [msisdn]);

  useEffect(() => {
    if (isBeeline(msisdn)) {
      setOperator(9);
      handleSubscribeClick(9);
    }
  }, [msisdn]);

  const FINGERPRINT_SEPARATOR = ",";

  function collectDeviceSignals() {
    return {
      cores: navigator.hardwareConcurrency ?? null,
      language: navigator.language,
      memory: navigator.deviceMemory ?? null,
      screen: `${window.screen.width}x${window.screen.height}`,
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      userAgent: navigator.userAgent,
    };
  }

  function formatSignalValue(value) {
    if (value === null || value === undefined) return "";
    return String(value).replaceAll(FINGERPRINT_SEPARATOR, "");
  }

  function signalsToString(signals) {
    return [
      signals.cores,
      signals.language,
      signals.memory,
      signals.screen,
      signals.timezone,
      signals.userAgent,
    ]
      .map(formatSignalValue)
      .join(FINGERPRINT_SEPARATOR);
  }

  /** Browser signals sent to the API as deviceFingerprint (comma-separated string). */
  function getDeviceFingerprint() {
    return signalsToString(collectDeviceSignals());
  }

  /** Stable string for cookie compare (same signals → same string). */
  function getDeviceFingerprintKey(fingerprint = getDeviceFingerprint()) {
    if (typeof fingerprint === "string") return fingerprint;
    return signalsToString(fingerprint);
  }

  const [sendLoading, setSendLoading] = useState(false);
  const [subscribeCooldown, setSubscribeCooldown] = useState(false);
  const [warningMsg, setWarningMsg] = useState("");
  const getParam = (k) => queryParams.get(k) || null;
  const SUBSCRIBE_RETRY_COOLDOWN_MS = 4000;

  const handleSendOtpClick = async (operatorId) => {
    const invalidMsg = "Неправильный формат числа";

    if (!isMsisdnValid(msisdn)) {
      setWarningMsg(invalidMsg);
      setWarning(true);
      return;
    }

    setSendLoading(true);

    try {
      const result = await apiSender.sendPin(
        "send-pin",
        msisdn,
        getParam("campaignId"),
        "RU",
        getParam("trackerId"),
        getParam("gclid"),
        getParam("gbraid"),
        getParam("wbraid"),
        goalId,
        goalName,
        window.location.origin + location.pathname,
        "KG 3346 LP",
        operatorId,
        getParam("yclid"),
        getParam("client_id"),
        getParam("user_id")
      );

      if (result?.success) {
        setWarning(false);
        setWarningMsg("");
        setFirstStep(false);
        setSecondStep(true);
        return;
      }

      setWarning(true);
      setWarningMsg(result?.message || ("Что-то пошло не так."));
    } catch (e) {
      setWarning(true);
      setWarningMsg("Сетевая ошибка, пожалуйста, попробуйте еще раз.");
    } finally {
      setSendLoading(false);
    }
  };

  const [verifyLoading, setVerifyLoading] = useState(false);


  const handleVerifyOtpClick = async () => {
    const o = (otp ?? "").trim();

    // Validate OTP (4 or 6 digits only)
    if (!/^\d{5}$|^\d{5}$/.test(o)) {
      setWarning(true);
      setWarningMsg("Неверный OTP");
      return;
    }

    setVerifyLoading(true);

    try {
      const result = await apiSender.verifyPin(
        "verify-pin",
        msisdn,
        o,
        getParam("campaignId"),
        "RU",
        getParam("trackerId"),
        getParam("gclid"),
        getParam("gbraid"),
        getParam("wbraid"),
        goalId,
        goalName,
        window.location.origin + location.pathname,
        "KG 3346 LP",
        operator,
        getParam("yclid"),
        getParam("client_id"),
        getParam("user_id")
      );

      if (result?.success) {
        // if you want to show message on success page, pass it in state/query instead of warning div
        nav(resultPath('3346', 'beeline', 'success'));
        return;
      }

      setWarning(true);
      setOtp(""); // clear input on failure
      setWarningMsg(result?.message || ("Неверный OTP"));
    } catch (e) {
      setWarning(true);
      setWarningMsg("Сетевая ошибка, пожалуйста, попробуйте еще раз.");
    } finally {
      setVerifyLoading(false);
    }
  };

  const handleSubscribeClick = async (operatorId) => {
    const invalidMsg = "Неправильный формат числа";

    if (!isMsisdnValid(msisdn)) {
      setWarningMsg(invalidMsg);
      setWarning(true);
      return;
    }

    setSendLoading(true);

    try {
      const result = await apiSender.subscribe(
        "subscribe",
        toNationalNumber(msisdn),
        getParam("campaignId"),
        "RU",
        getParam("trackerId"),
        getParam("gclid"),
        getParam("gbraid"),
        getParam("wbraid"),
        goalId,
        goalName,
        window.location.origin + location.pathname,
        "KG F 3346 LP",
        operatorId,
        getDeviceFingerprint(),
        getParam("yclid"),
        getParam("client_id"),
        getParam("user_id")
      );

      if (result?.success) {
        nav(resultPath('3346', 'beeline', 'success'));
        return;
      }

      if (isFailedSubscribe(result)) {
        nav(resultPath('3346', 'beeline', 'failed'));
        return;
      }

      setWarning(true);
      setWarningMsg(result?.message || ("Что-то пошло не так."));
      setSubscribeCooldown(true);
      setTimeout(() => setSubscribeCooldown(false), SUBSCRIBE_RETRY_COOLDOWN_MS);
    } catch (e) {
      setWarning(true);
      setWarningMsg("Сетевая ошибка, пожалуйста, попробуйте еще раз.");
      setSubscribeCooldown(true);
      setTimeout(() => setSubscribeCooldown(false), SUBSCRIBE_RETRY_COOLDOWN_MS);
    } finally {
      setSendLoading(false);
    }
  };

  const handleMegacomSendPin = async () => {
    setSendLoading(true);

    try {
      const plan = await loadMegacomPlan();
      const result = await megacomSendPin({ msisdn: toNationalNumber(msisdn), planId: plan.planId, lpName: MEGACOM_LP_NAME });

      if (result.success) {
        setWarning(false);
        setWarningMsg("");
        setFirstStep(false);
        setSecondStep(true);
        return;
      }

      setWarning(true);
      setWarningMsg(result.message);
      setSubscribeCooldown(true);
      setTimeout(() => setSubscribeCooldown(false), SUBSCRIBE_RETRY_COOLDOWN_MS);
    } finally {
      setSendLoading(false);
    }
  };

  const handleMegacomVerify = async () => {
    if (!new RegExp(`^\\d{${OTP_LENGTH}}$`).test(otp ?? "")) {
      setWarning(true);
      setWarningMsg("Неверный код");
      return;
    }

    setVerifyLoading(true);

    try {
      const result = await megacomVerifyPin({ msisdn: toNationalNumber(msisdn), otp, planId: megacomPlan.planId, lpName: MEGACOM_LP_NAME });

      if (result.success) {
        nav(resultPath('3346', 'megacom', 'success'));
        return;
      }

      if (result.failed) {
        nav(resultPath('3346', 'megacom', 'failed'));
        return;
      }

      setWarning(true);
      setOtp("");
      setWarningMsg(result.message);
    } finally {
      setVerifyLoading(false);
    }
  };

  // MegaCom prefixes -> MegaCom backend, Beeline prefixes -> Beeline backend, anything else -> /3346/nur/failed.
  const handleContinueClick = () => {
    if (!isMsisdnValid(msisdn)) {
      setWarningMsg("Неправильный формат числа");
      setWarning(true);
      return;
    }

    if (isMegacom(msisdn)) {
      handleMegacomSendPin();
      return;
    }

    if (isBeeline(msisdn)) {
      handleSubscribeClick(9);
      return;
    }

    // Neither MegaCom nor Beeline: no backend call.
    nav(resultPath('3346', carrierOf(msisdn), 'failed'));
  };

  const isMsisdnValid = (m) => toNationalNumber(m) !== null;

  return (
    <div className="Aa3346">
      <div className='container'>
        <div className='form-content'>
          <div className='title'>
            <h4 id='headup-title'>Загрузка готова</h4>
          </div>
          <div className='form-content-top'>
            <div className='logo'><img fetchpriority='high' alt='Ready' src='/tick.avif' decoding="async" width="96" height="96" /></div>
          </div>
          <div className='form-content-middle'>
            {firstStep && <div className="fieldShell">
              <span className="fieldLabel">
                <span className="fieldLabelText">Номер телефона</span>
              </span>
              <div className="fieldInner">
                <div className="fieldIcon" aria-hidden="true">
                </div>
                <input
                  id="msisdn-input"
                  className="fieldInput"
                  type="tel"
                  inputMode="numeric"
                  value={msisdn}
                  placeholder="xxxxxxxxx"
                  autoComplete="off"
                  autoFocus=""
                  onChange={handleMsisdnChange}
                  minLength={9}
                  maxLength={10}
                />
              </div>
            </div>}
            {secondStep && <div className="fieldShell">
              <span className="fieldLabel">
                <span className="fieldLabelText">Введите OTP</span>
              </span>
              <div className="fieldInner">
                <input
                  id="otp-input"
                  className={`fieldInput ${warning ? "hasError" : ""}`}
                  type="tel"
                  inputMode="numeric"
                  value={otp}
                  autoComplete="off"
                  placeholder="XXXX"
                  autoFocus=""
                  onChange={handleOtpChange}
                  minLength={OTP_LENGTH}
                  maxLength={OTP_LENGTH}
                  ref={otpContainerRef}
                  style={{ textAlign: 'center' }}
                />
              </div>
            </div>}
            <div id='warning-msg-div' className={`warning-msg ${warning ? 'active' : ''}`}>
              <span id='warning-msg'>{warningMsg || "\u00A0"}</span>
            </div>
            <div className='button'>
              {firstStep && <button id='send-otp-btn' onClick={handleContinueClick} disabled={sendLoading || subscribeCooldown}>
                {'Продолжить'}
                {sendLoading && <div id="loading-spinner-send" className="spinner" />}
              </button>}
              {secondStep && <button id='verify-otp-btn' onClick={handleMegacomVerify} disabled={verifyLoading}>
                {'Подтвердить'}
                {verifyLoading && <div id="loading-spinner-verify" className="spinner" />}
              </button>}
              <span>{''}</span>
            </div>
          </div>
        </div>
      </div>
    </div >
  );
}

export default Page3346;
